/**
 * Centralized save state store using Zustand
 * Replaces event relay pattern with direct function calls
 *
 * @date 2026/01/23
 */

import { toast } from 'react-toastify';
import { create } from 'zustand';

import { NO_CHANGES_SAVE } from '@/config';
import { log, warn } from '@/utils/logger';

import { muteSymbolsChanged } from '../bridge/eventsRelayer';
import { SaveOptions } from '../map/symbolSaver';
import { TKEvent } from '../types';

interface SaveStore {
  /** Is a save operation currently in progress */
  saving: boolean;
  /** Timestamp of last successful save */
  lastSaveTime: number | null;
  /** Number of symbols saved in last operation */
  lastSaveCount: number;
  /** Save progress percentage (0-100) driven by batch completion */
  saveProgress: number;

  // === Actions ===

  /**
   * Start save operation - shows spinner in UI
   */
  startSaving: () => void;

  /**
   * Finish save operation
   * - hides spinner and shows toast
   * - mute edit change state
   *
   * @param successCount Number of symbols successfully saved
   * @param silent If true, don't show toast notification
   */
  finishSaving: (successCount: number, silent?: boolean) => void;

  /**
   * Report progress as batches complete during a running save.
   * `saved` / `total` drives the green progress bar percentage.
   */
  updateSaveProgress: (saved: number, total: number) => void;

  /**
   * Validate symbols then dispatch SAVECHANGES to route through SavingQueueExtension.
   * The queue handles spinner, toast, and reload via startSaving/finishSaving.
   *
   * @param options Save options (projectId, fileId, pageId, symbols, urlParams)
   * @param silent If true, suppress the success toast
   */
  performSave: (options: SaveOptions, silent?: boolean) => void;

  /**
   * Reset store to initial state
   */
  reset: () => void;
}

export const useSaveStore = create<SaveStore>((set) => ({
  saving: false,
  lastSaveTime: null,
  lastSaveCount: 0,
  saveProgress: 0,

  startSaving: () => {
    log('>>> Starting save operation...');
    set({ saving: true, saveProgress: 0 });
  },

  updateSaveProgress: (saved: number, total: number) => {
    const progress = total > 0 ? Math.round((saved / total) * 100) : 0;
    set({ saveProgress: Math.min(progress, 99) });
  },

  finishSaving: (successCount: number, silent = false) => {
    log(`>>> Finishing save operation. Success count: ${successCount}`);

    // Silent mode - only mute changes if successful
    if (successCount > 0) {
      // log(`>>> Silent: ${silent}, ${successCount} symbols saved`);
      muteSymbolsChanged();
      // log(`## symbol change muted!`);
      set({
        saving: false,
        saveProgress: 100,
        lastSaveTime: Date.now(),
        lastSaveCount: successCount,
      });
      // collapse the progress bar after a short hold at 100%
      setTimeout(() => set({ saveProgress: 0 }), 500);
    }

    // Show toast notification based on mode
    if (!silent) {
      if (successCount > 0) {
        toast.success(`${successCount} symbol changes saved!`, {
          autoClose: 2000,
        });
      } else {
        // No changes to save
        toast.info(NO_CHANGES_SAVE);
      }
    }
  },

  performSave: (options: SaveOptions, silent = false) => {
    // Validate symbols — show "no changes" toast in manual mode
    if (!options.symbols || options.symbols.length === 0) {
      warn('## No symbols to save!');
      if (!silent) toast.info(NO_CHANGES_SAVE);
      return;
    }

    log(
      `>>> Routing ${options.symbols.length} symbol(s) through saving queue...`,
    );

    // Dispatch SAVECHANGES to enqueue the job in SavingQueueExtension.
    // onSymbolsChangesSaving() snapshots dirty symbols, pushes to queue, and
    // starts drainQueue() which calls startSaving()/finishSaving() directly.
    const event = new CustomEvent(TKEvent.SAVECHANGES, {
      detail: {
        projectId: options.projectId,
        fileId: options.fileId,
        pageId: options.pageId,
        silent,
      },
    });
    document.dispatchEvent(event);
  },

  reset: () => {
    set({
      saving: false,
      saveProgress: 0,
      lastSaveTime: null,
      lastSaveCount: 0,
    });
  },
}));

/**
 * Hook to access save store state and actions
 *
 * @example
 * ```tsx
 * // Get saving state
 * const saving = useSave(state => state.saving);
 *
 * // Get save function
 * const performSave = useSave(state => state.performSave);
 *
 * // Trigger save
 * await performSave({
 *   projectId,
 *   fileId,
 *   pageId,
 *   symbols: changedSymbols,
 *   urlParams: new URLSearchParams(window.location.search),
 * });
 * ```
 */
export const useSave = useSaveStore;
